import React from "react";
import Typography from "aldoo-ra/Typography";
import Input from "aldoo-ra/Input";
import Checkbox from "aldoo-ra/Checkbox";

/**
 * Export Plugin
 * Exports selected content items as an update package
 */
export default {
  id: "cms-export",
  name: "Export Items",
  description: "Export selected items as update package",
  category: "import-export",
  hidden: true,

  requiredCapabilities: {
    needsContentType: true,
    needsSelection: true,
    needsFieldAccess: false,
    needsTypeModification: false,
  },

  validate(context) {
    const errors = [];

    if (!context.contentType) {
      errors.push({ message: "Content type is required" });
    }

    if (!context.selectedItems || context.selectedItems.length === 0) {
      errors.push({ message: "Please select items to export" });
    }

    return {
      valid: errors.length === 0,
      errors: errors.length > 0 ? errors : undefined,
    };
  },

  getInterface(context) {
    return {
      dialog: {
        title: "Export Content Items",
        fields: [
          {
            id: "packageName",
            type: "custom",
            label: "Package Name",
            required: true,
            defaultValue: `export-${new Date().toISOString().split("T")[0]}`,
            render: (props) => (
              <div>
                <Typography variant="admin_label" className="mb-1">
                  Package Name
                  <span className="text-admin_error ml-1">*</span>
                </Typography>
                <Input
                  value={props.value || ""}
                  onChange={(e) => props.onChange(e.target.value)}
                  placeholder="export-package-name"
                  variant="admin"
                  className="w-full"
                />
                {props.error && (
                  <Typography variant="admin_caption" className="text-admin_error mt-1">
                    {props.error}
                  </Typography>
                )}
              </div>
            ),
          },
          {
            id: "includeType",
            type: "checkbox",
            label: "Include Content Type Definition",
            defaultValue: true,
          },
          {
            id: "includeRelated",
            type: "checkbox",
            label: "Include Related Content",
            defaultValue: false,
          },
          {
            id: "includeMedia",
            type: "checkbox",
            label: "Include Media Files",
            defaultValue: true,
          },
        ],
      },

      summary: {
        beforeExecute: (values, context) => ({
          itemsAffected: context.selectedItems.length,
          changes: [
            {
              description: `Export ${context.selectedItems.length} item(s) to "${values.packageName}"`,
              impact: "low",
            },
          ],
          warnings: values.includeMedia
            ? ["Large media files may increase export time"]
            : [],
        }),

        afterExecute: (result) => {
          if (result.success) {
            return `Successfully exported ${result.itemsProcessed} item(s) to ${result.data?.packageName}`;
          }
          return `Export failed: ${result.errors?.[0]?.error}`;
        },
      },
    };
  },

  async execute(context, params) {
    const { values } = params;
    const { packageName, includeType, includeRelated, includeMedia } = values;

    try {
      // Build export package
      const exportData = {
        name: packageName,
        version: "1.0.0",
        created: new Date().toISOString(),
        contentType: includeType ? context.contentType : null,
        items: context.selectedItems.map((item) => ({
          _id: item._id,
          name: item.name,
          content: item.content,
          createdAt: item.createdAt,
          updatedAt: item.updatedAt,
        })),
      };

      // If including related content, we'd need to traverse references
      // This is a simplified version
      if (includeRelated) {
        // TODO: Implement related content traversal
        context.ui.showNotification(
          "Related content export not yet implemented",
          "info"
        );
      }

      // Create downloadable file
      const blob = new Blob([JSON.stringify(exportData, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${packageName}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      context.ui.showNotification(
        `Exported ${context.selectedItems.length} item(s) to ${packageName}.json`,
        "success"
      );

      return {
        success: true,
        itemsProcessed: context.selectedItems.length,
        itemsSucceeded: context.selectedItems.length,
        itemsFailed: 0,
        data: { packageName },
      };
    } catch (error) {
      return {
        success: false,
        itemsProcessed: 0,
        itemsSucceeded: 0,
        itemsFailed: context.selectedItems.length,
        errors: [{ error: error.message }],
      };
    }
  },
};
